import { AddIcon } from "@chakra-ui/icons";
import {
    Box,
    Button,
    Container,
    Flex,
    FormControl,
    Heading,
    HStack,
    Icon,
    Image,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Spacer,
    Stack,
    Table,
    Tbody,
    Td,
    Text,
    Textarea,
    Th,
    Thead,
    Tr,
} from "@chakra-ui/react";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import imageCompression from "browser-image-compression";
import { useContext, useEffect, useState } from "react";
import { FaFileImage, FaTrash } from "react-icons/fa";
import { Context } from "../../ContextWrapper";
import CustomTableContainer from "../../theme/components/tableContainer";

export const TutorialItems = () => {
  const [loading, setLoading] = useState(true);
  const [tutorialList, setTutorialList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentTutorial, setCurrentTutorial] = useState({});
  const [toggleItems, setToggleItems] = useState(false);

  const context = useContext(Context);

  const compressImage = async (file) => {
    const options = {
      maxSizeMB: 0.3,
      maxWidthOrHeight: 800,
      useWebWorker: true,
    };
    try {
      const compressedBlob = await imageCompression(file, options);
      return new File([compressedBlob], file.name, {
        type: compressedBlob.type,
      });
    } catch (err) {
      console.error("Image compression failed", err);
      return file;
    }
  };

  const toggle = () => {
    setCurrentTutorial({});
    setIsModalOpen(!isModalOpen);
  };

  const startCreateTutorial = () => {
    setCurrentTutorial({});
    setIsModalOpen(true);
  };

  const startUpdateTutorial = (tutorialData) => {
    setCurrentTutorial(tutorialData);
    setIsModalOpen(true);
  };

  const toggleDelete = () => {
    setCurrentTutorial({});
    setIsDeleteModalOpen(!isDeleteModalOpen);
  };

  const startDeleteTutorial = (tutorialData) => {
    setCurrentTutorial(tutorialData);
    setIsDeleteModalOpen(true);
  };

  const toggleView = () => {
    setToggleItems(true);
  };

  const refreshTutorials = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/v2/admin-tutorial/", {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      setTutorialList(res.data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshTutorials();
  }, []);

  const handleSubmit = async (event, tutorial) => {
    event.preventDefault();
    setLoading(true);

    if (!tutorial.external_link) {
      context.fireToast("Error", "Tutorial must have a link", "error");
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("tutorial_title", tutorial.tutorial_title);
    formData.append("tutorial_description", tutorial.tutorial_description);
    formData.append("external_link", tutorial.external_link);
    formData.append("video_duration", tutorial.video_duration);
    formData.append("order", tutorial.order);

    if (tutorial.card_image instanceof File) {
      try {
        const compressedCardImage = await compressImage(tutorial.card_image);
        formData.append("card_image", compressedCardImage);
      } catch (error) {
        console.error("Card image compression failed:", error);
      }
    }

    let url = `/api/v2/admin-tutorial/`;
    try {
      let res;
      if (tutorial.id) {
        url += `${tutorial.id}/`;
        res = await axios.patch(url, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await axios.post(url, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      if (res.status < 300) {
        context.fireToast(
          "Success",
          tutorial.id ? "Tutorial updated" : "Tutorial added",
          "success"
        );
        toggle();
        await refreshTutorials();
      } else {
        context.fireToast("Error", res.request.responseText, "error");
      }
    } catch (err) {
      console.error("Error submitting tutorial:", err);
      context.fireToast("Error", "Failed to save tutorial", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (tutorialData) => {
    let url = `/api/v2/admin-tutorial/${tutorialData.id}/`;

    axios
      .delete(url)
      .then((res) => {
        if (res.status < 300) {
          setTutorialList(tutorialList.filter((o) => o.id !== tutorialData.id));
          context.fireToast("Success", "Tutorial deleted", "success");
        } else {
          context.fireToast("Error", res.request.responseText, "error");
        }
        setLoading(false);
        toggleDelete();
      })
      .catch((err) => console.log(err));
  };

  return (
    <Container maxW="100%" bg="brand.50" h="calc(91vh)">
      {!toggleItems && (
        <>
          {loading && (
            <FontAwesomeIcon
              className="fa-spin"
              icon={faSpinner}
              color="green"
            />
          )}
          <Stack direction="column" spacing={2} align="center">
            <CustomTableContainer width="100%">
              <TutorialModal
                isOpen={isModalOpen}
                toggle={toggle}
                title="Tutorial"
                tutorialMainData={currentTutorial}
                loading={loading}
                handleSubmit={handleSubmit}
              />
              <TutorialDeleteModal
                isOpen={isDeleteModalOpen}
                toggle={toggleDelete}
                tutorialMainData={currentTutorial}
                loading={loading}
                handleDelete={handleDelete}
              />
              <Flex pb="5">
                <Box>
                  <Heading as="h3" size="lg">
                    Tutorials
                  </Heading>
                </Box>
                <Spacer />
                <Box>
                  <HStack spacing={6}>
                    <Button
                      onClick={startCreateTutorial}
                      leftIcon={<AddIcon boxSize={3} />}
                    >
                      Create Tutorial
                    </Button>
                  </HStack>
                </Box>
              </Flex>
              <Box maxHeight="calc(100vh - 350px)" bg="white" overflowY="auto">
                <Table variant="unstyled">
                  <Thead position="sticky" top={-1} zIndex={1}>
                    <Tr>
                      <Th>Tutorial Title</Th>
                      <Th>Ordering</Th>
                      <Th>Action</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {tutorialList.map((tutorial) => (
                      <TutorialRow
                        key={tutorial.id}
                        tutorialMainData={tutorial}
                        startUpdateTutorial={startUpdateTutorial}
                        startDeleteTutorial={startDeleteTutorial}
                        toggle={toggleView}
                      />
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </CustomTableContainer>
          </Stack>
        </>
      )}
    </Container>
  );
};

const TutorialRow = ({
  tutorialMainData,
  startUpdateTutorial,
  startDeleteTutorial,
  toggle,
}) => {
  return (
    <Tr key={tutorialMainData.id}>
      <Td>{tutorialMainData.tutorial_title}</Td>
      <Td>{tutorialMainData.order}</Td>
      <Td>
        <HStack spacing={2}>
          <Button onClick={() => startUpdateTutorial(tutorialMainData)}>
            Edit
          </Button>
          <Button
            colorScheme="red"
            onClick={() => startDeleteTutorial(tutorialMainData)}
          >
            Delete
          </Button>
        </HStack>
      </Td>
    </Tr>
  );
};

const TutorialModal = ({
  isOpen,
  toggle,
  title,
  tutorialMainData,
  loading,
  handleSubmit,
}) => {
  const confirmText = !tutorialMainData?.id ? "Create" : "Update";
  const context = useContext(Context);

  const [tutorialData, setTutorialData] = useState({
    tutorial_title: "",
    tutorial_description: "",
    external_link: "",
    video_duration: "",
    is_active: true,
    card_image: "",
    order: "",
  });

  const [preview, setPreview] = useState(null);
  const [fileAttached, setFileAttached] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setTutorialData({
        tutorial_title: "",
        tutorial_description: "",
        external_link: "",
        video_duration: "",
        card_image: "",
        order: "",
        is_active: true,
      });
      setPreview(null);
      setFileAttached(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (tutorialMainData?.id) {
      setTutorialData(tutorialMainData);
      setPreview(tutorialMainData.card_image || null);
    }
  }, [tutorialMainData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTutorialData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    setPreview(previewUrl);
    setTutorialData((prev) => ({ ...prev, card_image: file }));
  };

  const removeImage = () => {
    setPreview(null);
    setTutorialData((prev) => ({ ...prev, card_image: "" }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSubmit(e, tutorialData);
  };

  return (
    <Modal isOpen={isOpen} onClose={toggle} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{confirmText + " " + title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody maxHeight="600px" overflowY="auto">
          <form id="TutorialForm" onSubmit={handleFormSubmit}>
            <FormControl mt={2}>
              <Text>Tutorial Title</Text>
              <Input
                type="text"
                name="tutorial_title"
                value={tutorialData.tutorial_title}
                onChange={handleInputChange}
                required
              />
            </FormControl>

            <FormControl mt={2}>
              <Text>Tutorial Description</Text>
              <Textarea
                name="tutorial_description"
                value={tutorialData.tutorial_description}
                onChange={handleInputChange}
                required
              />
            </FormControl>

            <FormControl mt={2}>
              <Text>Order</Text>
              <Input
                type="number"
                name="order"
                value={tutorialData.order}
                onChange={handleInputChange}
                required
              />
            </FormControl>

            <FormControl mt={2}>
              <Text>Thumbnail</Text>
              <Box
                borderWidth="2px"
                borderStyle="dashed"
                borderColor="gray"
                borderRadius="lg"
                backgroundColor="white"
                width="100%"
                maxW="400px"
                height="200px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
                cursor="pointer"
                _hover={{ borderColor: "blue.300" }}
                position="relative"
                overflow="hidden"
              >
                {preview || tutorialData?.card_image ? (
                  <Image
                    src={preview ? preview : tutorialData?.card_image}
                    alt="Uploaded preview"
                    maxH="100%"
                    maxW="100%"
                    objectFit="contain"
                    borderRadius="md"
                  />
                ) : (
                  <>
                    <Icon as={FaFileImage} boxSize={10} color="gray.400" />
                    <Text mt={2}>
                      <Text as="span" fontWeight="bold" color="blue.500">
                        Click to upload
                      </Text>{" "}
                      or drag and drop
                    </Text>
                  </>
                )}
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  opacity="0"
                  position="absolute"
                  width="100%"
                  height="100%"
                  cursor="pointer"
                  name="card_image"
                />
              </Box>
              {preview && (
                <Button
                  leftIcon={<FaTrash />}
                  colorScheme="red"
                  variant="outline"
                  mt={2}
                  onClick={removeImage}
                >
                  Remove File
                </Button>
              )}
            </FormControl>

            <FormControl mt={2}>
              <Text>Video Link</Text>
              <Input
                name="external_link"
                value={tutorialData.external_link}
                onChange={handleInputChange}
              />
            </FormControl>

            <FormControl mt={2}>
              <Text>Video Duration</Text>
              <Input
                name="video_duration"
                value={tutorialData.video_duration}
                onChange={handleInputChange}
              />
            </FormControl>
          </form>
        </ModalBody>

        <ModalFooter>
          <Button onClick={toggle} isDisabled={loading}>
            Cancel
          </Button>
          <Button type="submit" form="TutorialForm" isDisabled={loading} ml={2}>
            {loading ? "Loading..." : confirmText}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const TutorialDeleteModal = ({
  isOpen,
  toggle,
  tutorialMainData,
  loading,
  handleDelete,
}) => {
  return (
    <Modal isOpen={isOpen} onClose={toggle}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Delete Tutorial</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          Are you sure you want to delete this tutorial{" "}
          <strong>{tutorialMainData?.tutorial_title}</strong>?
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme="red"
            onClick={() => handleDelete(tutorialMainData)}
            isDisabled={loading}
          >
            {loading ? "Processing..." : "Delete"}
          </Button>
          <Button onClick={toggle} ml={3}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
